import React from 'react';

import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useThemeContext } from '@infoods/lifebase-ui';

import classes from './GrossRevenueGraph.module.scss';
import { GrossRevenueGraphData } from '../../../../interfaces/entity-dashboard/GrossRevenueData';
import { getGraphInfo } from './utils/getGraphInfo/getGraphInfo';
import { getTooltipLabel } from './utils/getTooltipLabel/getTooltipLabel';
import {
  getShowFirstAndLastLabelsParser,
  getTooltipBaseOptions,
} from '../../../../../shared/features/charts';
import { getAbbreviatedPriceLabel } from './utils/getAbbreviatedPriceLabel/getAbbreviatedPriceLabel';
import { getGraphPlugins } from './GrossRevenueGraph.settings';

ChartJS.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
);

type GrossRevenueGraphProps = {
  grossRevenueGraphData: GrossRevenueGraphData;
};

const GrossRevenueGraph = ({ grossRevenueGraphData }: GrossRevenueGraphProps) => {
  const { palette } = useThemeContext();

  const { labels, currentValues, previousValues, maxValue } =
    getGraphInfo(grossRevenueGraphData);

  const fontCaption12Medium = {
    family: "'Lato', sans-serif",
    size: 12,
    weight: 500,
  } as const;

  const ticksOptions = {
    color: palette.neutral[600],
    font: fontCaption12Medium,
    maxRotation: 0,
  };

  const graphPlugins = getGraphPlugins(palette);
  const tooltipBaseOptions = getTooltipBaseOptions(palette);

  return (
    <div className={classes.graph}>
      <Line
        data={{
          labels,
          datasets: [
            {
              data: currentValues,
              label: 'Current Period',
              borderColor: palette.success[400],
              borderWidth: 1.2,
            },
            {
              data: previousValues,
              label: 'Previous Period',
              borderColor: palette.neutral[300],
              borderDash: [4, 4],
              borderWidth: 1.2,
            },
          ],
        }}
        options={{
          animation: false,
          maintainAspectRatio: false,
          elements: {
            point: {
              radius: 0,
              hoverRadius: 0,
            },
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              border: { color: palette.neutral[200], width: 1, dash: [2, 2] },
              ticks: {
                ...ticksOptions,
                maxRotation: 0,
                autoSkip: false,
                align: 'inner',
                callback: getShowFirstAndLastLabelsParser(
                  (value) => labels[value as number],
                ),
                padding: 0,
              },
              grid: {
                tickColor: 'transparent',
                color: palette.neutral[200],
              },
            },
            y: {
              position: 'right',
              max: maxValue,
              beginAtZero: true,
              border: { color: palette.neutral[200], width: 1, dash: [2, 2] },
              grid: {
                tickColor: 'transparent',
                color: palette.neutral[200],
              },
              ticks: {
                ...ticksOptions,
                maxTicksLimit: 5,
                callback: (value) => getAbbreviatedPriceLabel(value),
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...tooltipBaseOptions,
              callbacks: {
                title: () => '',
                label: getTooltipLabel(
                  grossRevenueGraphData.data,
                  grossRevenueGraphData.groupBy,
                ),
              },
            },
          },
        }}
        plugins={graphPlugins}
      />
    </div>
  );
};

export { GrossRevenueGraph };
